import React, { useEffect, useState } from "react";
import "./Settings.css";
import {
  FaUser,
  FaPlus,
  FaCreditCard,
  FaBookmark,
  FaBriefcase,
  FaCog,
  FaRobot,
  FaShareAlt,
  FaQuestionCircle,
  FaSignOutAlt,
  FaChevronRight,
  FaUsers,
  FaCoins
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import ShareProfileModal from "../../components/ShareProfileModal/ShareProfileModal";
import { useLanguage } from '../../contexts/LanguageContext';
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";

const Settings = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, signOut: authSignOut } = useAuth();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const { translations: t, changeLanguage, language: currentLanguage } = useLanguage();
  const [showShareModal, setShowShareModal] = useState(false);
  const [isStoreEnabled, setIsStoreEnabled] = useState(false);
  const [showModeToast, setShowModeToast] = useState(false);
  const [modeToastText, setModeToastText] = useState("");
  const [showConfirmBusinessModal, setShowConfirmBusinessModal] = useState(false);
  const [isProcessingToggle, setIsProcessingToggle] = useState(false);

  // Build public ViewProfile URL for sharing (QR + copy link)
  // Simple format: https://iwinto.com/@username
  const profileUrl = (() => {
    if (!currentUser?.id || !userProfile) return '';

    const usernameSlug =
      userProfile.username ||
      userProfile.display_name ||
      (currentUser.user_metadata?.full_name || currentUser.user_metadata?.name) ||
      'user';

    const base =
      (process.env.REACT_APP_SHARE_BASE_URL &&
        process.env.REACT_APP_SHARE_BASE_URL.replace(/\/$/, '')) ||
      (typeof window !== 'undefined'
        ? window.location.origin.replace(/\/$/, '')
        : 'https://iwinto.com');

    // Simple format: /@username → handled by ViewProfile route
    return `${base}/@${encodeURIComponent(usernameSlug)}`;
  })();

  const languages = [
    { code: 'en', name: 'English',  english: 'English' },
    { code: 'fr', name: 'Français', english: 'French' },
    { code: 'ar', name: 'العربية',  english: 'Arabic' },
    { code: 'fa', name: 'فارسی',    english: 'Persian' },
    { code: 'es', name: 'Español',  english: 'Spanish' },
    { code: 'de', name: 'Deutsch',  english: 'German' },
    { code: 'tr', name: 'Türkçe',   english: 'Turkish' },
    { code: 'zh', name: '中文',       english: 'Chinese' },
    { code: 'ko', name: '한국어',     english: 'Korean' },
    { code: 'ru', name: 'Русский',   english: 'Russian' },
    { code: 'hi', name: 'हिन्दी',     english: 'Hindi' },
    { code: 'ur', name: 'اردو',       english: 'Urdu' },
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.id) return;

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('username, avatar_url, store_enabled')
          .eq('user_id', currentUser.id)
          .single();

        if (error) {
          console.error("Failed to fetch user data:", error);
          return;
        }

        if (profile) {
          setIsStoreEnabled(!!profile.store_enabled);
          // Username and photoURL are now handled by userProfile from AuthContext
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    fetchUserData();
  }, [currentUser?.id]);

  const handleLogout = async () => {
    await authSignOut();
    navigate("/auth");
  };

  const handleToggleBusinessAccount = async () => {
    try {
      if (!currentUser?.id) return;
      
      const next = !isStoreEnabled;
      const { error } = await supabase
        .from('profiles')
        .update({ store_enabled: next })
        .eq('user_id', currentUser.id);

      if (error) {
        console.error("Failed to toggle business account:", error);
        return;
      }

      setIsStoreEnabled(next);
      setModeToastText(
        next
          ? (t.activateBusinessAccount || "Switched to Business Account")
          : (t.deactivateBusinessAccount || "Switched to Regular User")
      );
      setShowModeToast(true);
      setTimeout(() => setShowModeToast(false), 3000);
    } catch (error) {
      console.error("Failed to toggle business account:", error);
    }
  };

  const openConfirmBusinessModal = () => setShowConfirmBusinessModal(true);

  const handleConfirmBusinessToggle = async () => {
    try {
      setIsProcessingToggle(true);
      await handleToggleBusinessAccount();
    } finally {
      setIsProcessingToggle(false);
      setShowConfirmBusinessModal(false);
    }
  };

  const handleCancelBusinessToggle = () => setShowConfirmBusinessModal(false);

  return (
    <div className="settings-container">
      <FloatingSidebarButton userProfile={userProfile} />

      <div className="settings-header">
        <button
          className="settings-back-button"
          onClick={() => navigate(-1)}
          aria-label={t.back || "Back"}
          title={t.back || "Back"}
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </button>
        <h2>{t.settings || "Settings"}</h2>
      </div>

      <div className="user-profile">
        <div className="avatar">
          {userProfile?.avatar_url ? (
            <img
              src={userProfile.avatar_url}
              alt={t.avatarAlt || "Avatar"}
              className="avatar-img"
              style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
            />
          ) : (
            (userProfile?.display_name || (t.userFallback || "User"))?.charAt(0).toUpperCase()
          )}
        </div>
        <div className="user-info">
          <h3>{userProfile?.display_name || (t.userFallback || "User")}</h3>
        </div>
      </div>

      <div className="settings-actions">
        <div className="action-item" onClick={() => navigate("/profile")}>
          <div className="settings-action-icon">
            <FaUser />
          </div>
          <span>{t.profile || "Profile"}</span>
        </div>

        <div className="action-item" onClick={() => navigate("/create")}>
          <div className="settings-action-icon">
            <FaPlus />
          </div>
          <span>{t.post || "Post"}</span>
        </div>

        <div className="action-item" onClick={() => navigate("/deposit")}>
          <div className="settings-action-icon">
            <FaCreditCard />
          </div>
          <span>{t.deposit || "Deposit"}</span>
        </div>

        <div className="action-item" onClick={() => navigate("/wallet")}>
          <div className="settings-action-icon">
            <FaCoins />
          </div>
          <span>{t.myWinis || "My Winis"}</span>
        </div>
      </div>

      <div className="settings-section">
        <h4 className="section-title">{t.myAccount || "My Account"}</h4>
        <div className="section-content">
          <div className="list-item" onClick={() => navigate("/follows")}>
            <div className="list-icon">
              <FaUsers />
            </div>
            <span>{t.follows || "Follows"}</span>
            <FaChevronRight className="chevron" />
          </div>

          <div className="list-item" onClick={() => navigate("/saved")}>
            <div className="list-icon">
              <FaBookmark />
            </div>
            <span>{t.savedPosts || "Saved Posts"}</span>
            <FaChevronRight className="chevron" />
          </div>

          <div className="list-item" onClick={openConfirmBusinessModal}>
            <div className="list-icon">
              <FaBriefcase />
            </div>
            <span>
              {isStoreEnabled
                ? (t.deactivateBusinessAccount || "Deactivate Business Account")
                : (t.activateBusinessAccount || "Activate Business Account")}
            </span>
            <FaChevronRight className="chevron" />
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h4 className="section-title">{t.appSettings || "App Settings"}</h4>
        <div className="section-content">
          <div className="list-item">
            <div className="list-icon">
              <FaRobot />
            </div>
            <span>{t.aiControls || "AI Controls"}</span>
            <FaChevronRight className="chevron" />
          </div>

          <div className="list-item" onClick={() => navigate("/account-settings") }>
            <div className="list-icon">
              <FaCog />
            </div>
            <span>{t.accountSettings || "Account Settings"}</span>
            <FaChevronRight className="chevron" />
          </div>

          <div className="list-item" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
            <div className="list-icon">🌐</div>
            <span>{t.language || "Language"}</span>
            <FaChevronRight className="chevron" />
          </div>

          {showLanguageDropdown && (
            <div className="settings-language-dropdown">
              {languages.map((lang) => (
                <div
                  key={lang.code}
                  className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setShowLanguageDropdown(false);
                  }}
                >
                  <div className="lang-texts">
                    <div className="lang-option-title">{lang.name}</div>
                    <div className="lang-option-subtitle">{lang.english}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="settings-section">
        <h4 className="section-title">{t.others || "Others"}</h4>
        <div className="section-content">
          <div className="list-item" onClick={() => setShowShareModal(true)}>
            <div className="list-icon">
              <FaShareAlt />
            </div>
            <span>{t.shareProfile || "Share My Profile"}</span>
            <FaChevronRight className="chevron" />
          </div>

          <div className="list-item" onClick={() => navigate("/help&feedbacks")}>
            <div className="list-icon">
              <FaQuestionCircle />
            </div>
            <span>{t.helpFeedback || "Help & Feedback"}</span>
            <FaChevronRight className="chevron" />
          </div>

          <div className="list-item logout" onClick={handleLogout}>
            <div className="list-icon">
              <FaSignOutAlt />
            </div>
            <span>{t.logout || "Logout"}</span>
            <FaChevronRight className="chevron" />
          </div>
        </div>
      </div>

      {showShareModal && (
        <ShareProfileModal
          onClose={() => setShowShareModal(false)}
          profileUrl={profileUrl}
        />
      )}

      {showModeToast && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            background: 'rgba(17,24,39,0.95)',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: 12,
            fontSize: 14,
            boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            zIndex: 9999
          }}
        >
          {modeToastText}
        </div>
      )}

      {showConfirmBusinessModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              margin: '0 16px',
              background: 'rgba(17,24,39,0.98)',
              color: '#e5e7eb',
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 20px 50px rgba(0,0,0,0.4)'
            }}
          >
            <div style={{ padding: '20px 20px 0 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'rgba(59,130,246,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--primary-color, #2563eb)'
                }}
              >
                <FaBriefcase />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>
                  {isStoreEnabled ? (t.deactivateBusinessAccount || 'Deactivate Business Account') : (t.activateBusinessAccount || 'Activate Business Account')}
                </div>
                <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 6 }}>
                  {isStoreEnabled
                    ? (t.confirmBusinessDeactivateQuestion || 'Are you sure you want to deactivate your Business Account?')
                    : (t.confirmBusinessActivateQuestion || 'Are you sure you want to activate your Business Account?')}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: 20, justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelBusinessToggle}
                style={{
                  background: 'transparent',
                  color: '#e5e7eb',
                  border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {t.cancel || 'Cancel'}
              </button>
              <button
                onClick={handleConfirmBusinessToggle}
                disabled={isProcessingToggle}
                style={{
                  background: 'var(--primary-color, #2563eb)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 600,
                  cursor: isProcessingToggle ? 'not-allowed' : 'pointer',
                  boxShadow: '0 8px 20px rgba(37,99,235,0.45)',
                  opacity: isProcessingToggle ? 0.8 : 1
                }}
              >
                {isProcessingToggle ? (t.processing || 'Processing...') : (t.confirm || 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
